import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useMenuLabelStore } from '@/store/menuLabel.store';
import { showApiErrorToast } from '@/utils/errors';
import { menuLabelQueryKeys, setMenuLabels } from '../api/menuLabels.api';
import { MENU_LABEL_FILTER, type MenuLabelFilter } from '../constants/menuLabels.constants';
import type { MenuNameActions } from '../types/menuLabel.types';
import {
  buildMenuLabelUnits,
  countMenuLabelRows,
  filterMenuLabelUnits,
  hasDuplicateNames,
  toMenuLabelChanges,
  toPendingLabels,
} from '../utils/menuLabelRows';

export function useMenuLabelsSetup() {
  const queryClient = useQueryClient();
  const labels = useMenuLabelStore((state) => state.labels);
  const setLabels = useMenuLabelStore((state) => state.setLabels);
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<MenuLabelFilter>(MENU_LABEL_FILTER.all);
  const [chosenUnitKey, setChosenUnitKey] = useState<string | null>(null);

  const allUnits = useMemo(() => buildMenuLabelUnits(labels, edited), [labels, edited]);
  const units = useMemo(
    () => filterMenuLabelUnits(allUnits, searchText, filter),
    [allUnits, searchText, filter],
  );
  const filterCounts = useMemo(
    () => ({
      [MENU_LABEL_FILTER.all]: countMenuLabelRows(
        filterMenuLabelUnits(allUnits, searchText, MENU_LABEL_FILTER.all),
      ),
      [MENU_LABEL_FILTER.renamed]: countMenuLabelRows(
        filterMenuLabelUnits(allUnits, searchText, MENU_LABEL_FILTER.renamed),
      ),
    }),
    [allUnits, searchText],
  );
  const changes = useMemo(() => toMenuLabelChanges(allUnits), [allUnits]);
  const hasDuplicates = useMemo(() => hasDuplicateNames(allUnits), [allUnits]);
  const selectedUnit = units.find((unit) => unit.unit.menuKey === chosenUnitKey) ?? units[0];

  const mutation = useMutation({
    mutationFn: (pending: Record<string, string>) =>
      setMenuLabels(Object.entries(pending).map(([menuKey, label]) => ({ menuKey, label }))),
    onSuccess: async (_data, pending) => {
      setLabels(pending);
      setEdited({});
      toast.success('Menu names saved');
      await queryClient.invalidateQueries({ queryKey: menuLabelQueryKeys.all() });
    },
    onError: (error) => showApiErrorToast(error, 'Could not save the menu names'),
  });

  function keepChosenUnitVisible(nextSearchText: string, nextFilter: MenuLabelFilter) {
    const visibleUnits = filterMenuLabelUnits(allUnits, nextSearchText, nextFilter);
    const currentKey = selectedUnit?.unit.menuKey ?? null;
    const isCurrentVisible = visibleUnits.some((unit) => unit.unit.menuKey === currentKey);
    setChosenUnitKey(isCurrentVisible ? currentKey : (visibleUnits[0]?.unit.menuKey ?? currentKey));
  }

  function changeSearchText(nextSearchText: string) {
    setSearchText(nextSearchText);
    keepChosenUnitVisible(nextSearchText, filter);
  }

  function changeFilter(nextFilter: MenuLabelFilter) {
    setFilter(nextFilter);
    keepChosenUnitVisible(searchText, nextFilter);
  }

  const nameActions = useMemo<MenuNameActions>(
    () => ({
      onRename: (menuKey, label) => setEdited((current) => ({ ...current, [menuKey]: label })),
      onUndo: (menuKey) =>
        setEdited((current) => {
          const next = { ...current };
          delete next[menuKey];
          return next;
        }),
      onReset: (menuKey) =>
        setEdited((current) => {
          const next = { ...current };
          if (labels[menuKey] === undefined) delete next[menuKey];
          else next[menuKey] = '';
          return next;
        }),
    }),
    [labels],
  );

  return {
    units,
    selectedUnit,
    chooseUnit: setChosenUnitKey,
    searchText,
    setSearchText: changeSearchText,
    filter,
    setFilter: changeFilter,
    filterCounts,
    nameActions,
    changes,
    hasDuplicates,
    save: () => mutation.mutate(toPendingLabels(labels, allUnits)),
    isSaving: mutation.isPending,
  };
}
